import React, { useMemo } from 'react';
import { LeadRecord, OpportunityRecord, LeadSource } from '../../types/throughput';
import { Card, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { motion } from 'framer-motion';
interface LeadSourceBreakdownProps {
  leads: LeadRecord[];
  opportunities: OpportunityRecord[];
  onSourceClick: (source: LeadSource) => void;
  selectedSource?: LeadSource | null;
}
type SourceMetrics = {
  source: LeadSource;
  created: number;
  converted: number;
  funded: number;
  conversionRate: number;
  fundedRate: number;
};
const SOURCE_COLORS: Record<LeadSource, string> = {
  Web: '#0176D3',
  Referral: '#04844B',
  Partner: '#9050E9',
  Phone: '#FF5D2D',
  Email: '#0D9DDA',
  Other: '#706E6B'
};
export function LeadSourceBreakdown({
  leads,
  opportunities,
  onSourceClick,
  selectedSource
}: LeadSourceBreakdownProps) {
  const sourceMetrics: SourceMetrics[] = useMemo(() => {
    const sources: LeadSource[] = ['Web', 'Referral', 'Partner', 'Phone', 'Email', 'Other'];
    return sources.map(source => {
      const sourceLeads = leads.filter(l => l.source === source);
      const sourceLeadIds = new Set(sourceLeads.map(l => l.id));
      const sourceOpps = opportunities.filter(o => sourceLeadIds.has(o.leadId));
      const fundedOpps = sourceOpps.filter(o => o.fundedDate);
      const created = sourceLeads.length;
      const converted = sourceOpps.length;
      const funded = fundedOpps.length;
      return {
        source,
        created,
        converted,
        funded,
        conversionRate: created > 0 ? converted / created * 100 : 0,
        fundedRate: created > 0 ? funded / created * 100 : 0
      };
    }).filter(m => m.created > 0).sort((a, b) => b.funded - a.funded);
  }, [leads, opportunities]);
  const avgFundedRate = sourceMetrics.length > 0 ? sourceMetrics.reduce((sum, m) => sum + m.fundedRate, 0) / sourceMetrics.length : 0;
  const getTrendIndicator = (rate: number) => {
    const diff = rate - avgFundedRate;
    if (diff > 3) return {
      icon: TrendingUp,
      color: 'text-[#04844B]',
      bg: 'bg-[#04844B]/10'
    };
    if (diff < -3) return {
      icon: TrendingDown,
      color: 'text-[#EA001E]',
      bg: 'bg-[#EA001E]/10'
    };
    return {
      icon: Minus,
      color: 'text-[#706E6B]',
      bg: 'bg-[#706E6B]/10'
    };
  };
  if (sourceMetrics.length === 0) {
    return <Card className="mb-6" data-id="element-158">
        <div className="py-12 text-center text-[#706E6B]" data-id="element-159">
          No lead source data available for the selected filters.
        </div>
      </Card>;
  }
  const totalFunded = sourceMetrics.reduce((sum, m) => sum + m.funded, 0);
  return <Card className="mb-6 p-0 overflow-hidden" noPadding data-id="element-160">
      <CardHeader bordered className="!mb-0" data-id="element-161">
        <div className="flex items-center gap-3" data-id="element-162">
          <CardTitle data-id="element-163">Lead Source Breakdown</CardTitle>
          {selectedSource && <Badge variant="brand" className="cursor-pointer hover:bg-[#0176D3]/20 transition-colors" onClick={() => onSourceClick(selectedSource)} data-id="element-164">
              Filtered: {selectedSource} ✕
            </Badge>}
        </div>
        <div className="text-xs text-[#706E6B]" data-id="element-165">
          Click a row to filter dashboard
        </div>
      </CardHeader>

      <div className="overflow-x-auto" data-id="element-166">
        <table className="w-full text-sm" data-id="element-167">
          <thead className="text-xs text-[#706E6B] uppercase bg-[#FAFAFA] border-b border-[#E5E5E5]" data-id="element-168">
            <tr data-id="element-169">
              <th className="px-5 py-3 font-semibold text-left" data-id="element-170">Source</th>
              <th className="px-4 py-3 font-semibold text-right" data-id="element-171">Created</th>
              <th className="px-4 py-3 font-semibold text-right" data-id="element-172">Converted</th>
              <th className="px-4 py-3 font-semibold text-right text-[#04844B]" data-id="element-173">
                Funded
              </th>
              <th className="px-4 py-3 font-semibold text-right" data-id="element-174">Conv. Rate</th>
              <th className="px-4 py-3 font-semibold text-right" data-id="element-175">
                Pull-through
              </th>
              <th className="px-4 py-3 font-semibold text-center" data-id="element-176">vs Avg</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E5]" data-id="element-177">
            {sourceMetrics.map((row, index) => {
            const trend = getTrendIndicator(row.fundedRate);
            const TrendIcon = trend.icon;
            const isSelected = selectedSource === row.source;
            return <motion.tr key={row.source} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.04
            }} className={`bg-white hover:bg-[#F5F9FD] transition-colors cursor-pointer ${isSelected ? 'bg-[#F5F9FD] ring-1 ring-inset ring-[#0176D3]' : ''}`} onClick={() => onSourceClick(row.source)} data-id="element-178">
                  <td className="px-5 py-3" data-id="element-179">
                    <div className="flex items-center gap-3" data-id="element-180">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{
                    backgroundColor: SOURCE_COLORS[row.source]
                  }} data-id="element-181" />
                      <span className="font-medium text-[#181818]" data-id="element-182">
                        {row.source}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#181818]" data-id="element-183">
                    {row.created.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#181818]" data-id="element-184">
                    {row.converted.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#04844B]" data-id="element-185">
                    {row.funded.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#706E6B]" data-id="element-186">
                    {row.conversionRate.toFixed(1)}%
                  </td>
                  <td className="px-4 py-3" data-id="element-187">
                    <div className="flex items-center justify-end gap-2" data-id="element-188">
                      <div className="w-14 h-1.5 bg-[#E5E5E5] rounded-full overflow-hidden" data-id="element-189">
                        <div className="h-full bg-[#04844B] rounded-full transition-all duration-300" style={{
                      width: `${Math.min(row.fundedRate, 100)}%`
                    }} data-id="element-190" />
                      </div>
                      <span className="text-xs font-medium tabular-nums w-10 text-right text-[#181818]" data-id="element-191">
                        {row.fundedRate.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-192">
                    <div className="flex justify-center" data-id="element-193">
                      <div className={`w-6 h-6 rounded-full ${trend.bg} flex items-center justify-center`} data-id="element-194">
                        <TrendIcon className={`h-3.5 w-3.5 ${trend.color}`} data-id="element-195" />
                      </div>
                    </div>
                  </td>
                </motion.tr>;
          })}
          </tbody>
        </table>
      </div>

      {/* Distribution Bar */}
      <div className="px-5 py-4 border-t border-[#E5E5E5] bg-[#FAFAFA]" data-id="element-196">
        <div className="flex items-center justify-between mb-2" data-id="element-197">
          <span className="text-xs font-medium text-[#706E6B]" data-id="element-198">
            Funded Distribution
          </span>
          <span className="text-xs text-[#706E6B]" data-id="element-199">
            {totalFunded} total funded
          </span>
        </div>
        <div className="flex h-3 rounded-lg overflow-hidden bg-[#E5E5E5]" data-id="element-200">
          {sourceMetrics.map(row => {
          const percentage = totalFunded > 0 ? row.funded / totalFunded * 100 : 0;
          if (percentage < 0.5) return null;
          return <motion.div key={row.source} initial={{
            width: 0
          }} animate={{
            width: `${percentage}%`
          }} transition={{
            duration: 0.4,
            delay: 0.1
          }} className="h-full flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity relative group" style={{
            backgroundColor: SOURCE_COLORS[row.source]
          }} onClick={() => onSourceClick(row.source)} data-id="element-201">
                {percentage > 10 && <span className="text-[10px] font-medium text-white" data-id="element-202">
                    {row.source}
                  </span>}
              </motion.div>;
        })}
        </div>
      </div>
    </Card>;
}